import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Analytics } from '@/lib/analytics';
import { parseGoalsFromText } from '@/utils/goalParser';

export interface Goal {
  id: string;
  goal_type: string;
  exercise_name: string | null;
  target_value: number;
  target_unit: string;
  current_value: number;
  starting_value: number | null;
  target_date: string | null;
  is_active: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Milestone {
  id: string;
  goal_id: string;
  milestone_type: string;
  achievement_value: number | null;
  achievement_unit: string | null;
  percentage_achieved: number | null;
  celebration_shown: boolean;
  achieved_at: string;
  created_at?: string | null;
}

export const useGoals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGoals = async (opts?: { silent?: boolean }) => {
    if (!user) return;

    // Only flash the loading state on the first load. Mutations update local
    // state immediately so a refetch must not blank the list.
    if (!opts?.silent) setLoading(true);
    try {
      const { data: goalsData, error: goalsError } = await supabase
        .from('user_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (goalsError) throw goalsError;
      setGoals(goalsData || []);

      // Fetch milestones
      const { data: milestonesData, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('user_id', user.id)
        .eq('celebration_shown', false)
        .order('achieved_at', { ascending: false });

      if (milestonesError) throw milestonesError;
      setMilestones(milestonesData || []);

    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  const createGoalsFromText = async (specificGoalsText: string, maxGoals?: number) => {
    if (!user || !specificGoalsText?.trim()) return [];

    try {
      // Never create duplicates if the user already has active goals
      const { count: existingCount } = await supabase
        .from('user_goals')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if ((existingCount ?? 0) > 0) {
        console.log('[Goals] Skipping createGoalsFromText — goals already exist');
        return [];
      }

      const parsedGoals = parseGoalsFromText(specificGoalsText);
      const limited =
        typeof maxGoals === 'number' && maxGoals > 0
          ? parsedGoals.slice(0, maxGoals)
          : parsedGoals.slice(0, 1); // default: at most one when seeding from text
      const createdGoals = [];

      for (const parsedGoal of limited) {
        try {
          const { data, error } = await supabase
            .from('user_goals')
            .insert([{
              ...parsedGoal,
              user_id: user.id
            }])
            .select()
            .single();

          if (error) throw error;
          createdGoals.push(data);
        } catch (error) {
          console.error('Error creating individual goal:', error);
        }
      }

      await fetchGoals({ silent: true });
      return createdGoals;
    } catch (error) {
      console.error('Error creating goals from text:', error);
      throw error;
    }
  };

  const createGoal = async (goalData: Omit<Goal, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user) {
      console.error('No user found when trying to create goal');
      throw new Error('You must be logged in to create a goal');
    }

    const now = new Date().toISOString();
    const tempId = `temp-${Date.now()}`;
    const startingValue = goalData.starting_value ?? goalData.current_value;
    const optimistic: Goal = {
      id: tempId,
      goal_type: goalData.goal_type,
      exercise_name: goalData.exercise_name,
      target_value: goalData.target_value,
      target_unit: goalData.target_unit,
      current_value: goalData.current_value,
      starting_value: startingValue,
      target_date: goalData.target_date,
      is_active: goalData.is_active !== false,
      description: goalData.description,
      created_at: now,
      updated_at: now,
    };

    // Paint the card immediately. Persist in the background — the old path
    // waited on a premium profile fetch, a count query, the insert, then a
    // full goals+milestones refetch that also flipped loading=true.
    setGoals((prev) => [optimistic, ...prev]);

    try {
      const { data, error } = await supabase
        .from('user_goals')
        .insert([{
          ...goalData,
          user_id: user.id,
          starting_value: startingValue,
        }])
        .select()
        .single();

      if (error) {
        console.error('Database error creating goal:', error);
        throw new Error(error.message || 'Failed to create goal in database');
      }

      setGoals((prev) => {
        if (!prev.some((g) => g.id === tempId)) {
          // User deleted the optimistic row before insert returned.
          void supabase
            .from('user_goals')
            .update({ is_active: false })
            .eq('id', data.id)
            .eq('user_id', user.id);
          return prev;
        }
        return prev.map((g) => (g.id === tempId ? data : g));
      });
      Analytics.goalCreated(goalData.goal_type, goalData.exercise_name || 'General');
      return data;
    } catch (error: any) {
      setGoals((prev) => prev.filter((g) => g.id !== tempId));
      console.error('Error creating goal:', error);
      throw error;
    }
  };

  const updateGoalProgress = async (goalId: string, newValue: number) => {
    if (!user) return;

    try {
      // If this goal was created before we started storing starting_value,
      // lock the current value as the starting point on the first progress update.
      const goal = goals.find(g => g.id === goalId);

      const updatePayload: Record<string, any> = { current_value: newValue };
      if (goal && goal.starting_value == null && goal.current_value != null) {
        updatePayload.starting_value = goal.current_value;
      }

      const { error } = await supabase
        .from('user_goals')
        .update(updatePayload)
        .eq('id', goalId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      // Check for milestones
      if (goal) {
        const goalType = goal.goal_type?.toLowerCase();
        const unit = goal.target_unit?.toLowerCase() || '';
        const name = goal.exercise_name?.toLowerCase() || '';
        
        // Determine if this is a "lower is better" goal
        const isLowerBetter = 
          goalType === 'running' ||
          (goalType === 'endurance' && (unit === 'minutes' || unit === 'min' || unit === 'seconds' || unit === 'sec')) ||
          goalType === 'weight' && goal.target_value < goal.current_value || // Weight loss
          name.includes('body fat');
        
        const isImprovement = isLowerBetter 
          ? newValue < goal.current_value 
          : newValue > goal.current_value;
          
        if (isImprovement) {
          // Create milestone entry to trigger milestone function
          const { error: milestoneError } = await supabase
            .from('milestones')
            .insert([{
              user_id: user.id,
              goal_id: goalId,
              milestone_type: 'personal_best',
              achievement_value: newValue,
              achievement_unit: goal.target_unit
            }]);

          if (milestoneError && !milestoneError.message.includes('duplicate')) {
            console.error('Error creating milestone:', milestoneError);
          } else if (!milestoneError) {
            Analytics.milestoneReached('personal_best', goal.goal_type);
          }
        }
        
        // Check if goal is achieved
        const isGoalAchieved = isLowerBetter 
          ? newValue <= goal.target_value 
          : newValue >= goal.target_value;
          
        if (isGoalAchieved) {
          Analytics.goalAchieved(goal.goal_type, 0); // daysToAchieve would need to be calculated
        }
        
        Analytics.goalUpdated(goal.goal_type, goal.exercise_name || 'General');
      }

      setGoals((prev) =>
        prev.map((g) =>
          g.id === goalId
            ? {
                ...g,
                current_value: newValue,
                starting_value:
                  g.starting_value == null && g.current_value != null
                    ? g.current_value
                    : g.starting_value,
              }
            : g,
        ),
      );
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  };

  const markMilestoneAsShown = async (milestoneId: string) => {
    try {
      const { error } = await supabase
        .from('milestones')
        .update({ celebration_shown: true })
        .eq('id', milestoneId);

      if (error) throw error;
      
      setMilestones(prev => prev.filter(m => m.id !== milestoneId));
    } catch (error) {
      console.error('Error marking milestone as shown:', error);
    }
  };

  const deleteGoal = async (goalId: string) => {
    if (!user) {
      throw new Error('You must be logged in to delete a goal');
    }
    if (!goalId) {
      throw new Error('Missing goal id');
    }

    const previous = goals;
    setGoals((prev) => prev.filter((g) => g.id !== goalId));

    try {
      // Soft-delete only. Hard delete was racing Progress.tsx, which re-seeded
      // a goal from profiles.specific_goals whenever the active list hit 0.
      const { error: softError } = await supabase
        .from('user_goals')
        .update({ is_active: false })
        .eq('id', goalId)
        .eq('user_id', user.id);

      if (softError) throw softError;
    } catch (error) {
      setGoals(previous);
      console.error('Error deleting goal:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      fetchGoals();
    }
  }, [user]);

  return {
    goals,
    milestones,
    loading,
    createGoal,
    createGoalsFromText,
    updateGoalProgress,
    deleteGoal,
    markMilestoneAsShown,
    refetch: fetchGoals
  };
};